import { useFormatAmount } from '@capsicum/common';
import Decimal from 'decimal.js-light';
import { useMemo } from 'react';

import { usePartners } from '../../partners';
import { useMainCurrenciesQuery } from '../../wallet/data-access/queries/useMainCurrenciesQuery';
import { type MarketplaceFilters } from '../models';

import { useAvailableMarkets } from './useAvailableMarkets';

export const useAvailableMarketplaceMarkets = (query?: MarketplaceFilters) => {
	const { getPartnerBy } = usePartners();
	const { formatAmount } = useFormatAmount();
	const { data: currencies } = useMainCurrenciesQuery();
	const { altCurrency, defaultCurrency } = currencies ?? {};

	// Remove empty values from query
	if (query?.currencyName === '') delete query.currencyName;
	if (query?.sportIds && query?.sportIds?.length === 0) delete query.sportIds;
	if (query?.partnerIds && query?.partnerIds?.length === 0) delete query.partnerIds;

	const { availableMarkets, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading } =
		useAvailableMarkets(query);

	const markets = useMemo(
		() =>
			availableMarkets?.map(market => {
				const partner = getPartnerBy(partner => partner.currencyId === market.baseCurrency?.id);

				return {
					baseCurrency: market.baseCurrency,
					id: market.id,
					isBuyAllowed: market.isBuyAllowed,
					isPercentagePositive: new Decimal(market.ticker?.evolutionPercentage || '0').gte(0),
					isPercentageZero: new Decimal(market.ticker?.evolutionPercentage || '0').eq(0),
					isSellAllowed: market.isSellAllowed,
					lastPriceDiff: new Decimal(market.ticker?.lastPrice || '0')
						.minus(market.ticker?.openPrice || '0')
						.toString(),
					logo:
						market.baseCurrency?.images?.find(image => image.imageType?.type === 'currency_logo')?.url ??
						partner?.images?.partnerLogo?.url,
					name: partner?.name ?? market.baseCurrency?.shortCode,

					partnerID: partner?.id,

					ticker: market.ticker
				};
			}),
		[getPartnerBy, availableMarkets]
	);

	return {
		altCurrency,
		defaultCurrency,
		fetchNextPage,
		formatAmount,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		isLoading,
		markets
	};
};
